import { ShareTicketBody } from "@mg/schemas/src/christo/catalyst";
import { EnterpriseProfileType } from "@mg/schemas/src/commons";
import {
  ArrowLeft,
  ArrowRight,
  UploadSimple,
  UserCircle,
  Link as LinkIcon,
  ShareNetwork,
  Export,
} from "@phosphor-icons/react";
import { Button, Flex } from "@radix-ui/themes";
import cx from "classnames";
import { usePostHog } from "posthog-js/react";
import React, { useMemo, useState, useEffect } from "react";

import CreateVersionDialog, { Asset } from "./dialogs/CreateRevisionDialog";
import SummarizeIntegrationResponseDialog from "./dialogs/SummarizeIntegrationResponseDialog";
import MissingFontsIndicator from "./MissingFontsIndicator";
import VersionSelect from "./VersionSelect";

import { AuthTooltip } from "../../../../../components/AuthTooltip";
import { PageHeader } from "../../../../../components/PageHeader";
import { TimeSavedBubble } from "../../../../../components/TimeSavedBubble";
import { useUI } from "../../../../../contexts/ui";
import { useAnalytics } from "../../../../../utils/analytics";
import {
  canAccessPunttDashboard,
  canUploadTickets,
} from "../../../../../utils/auth";
import { isNil } from "../../../../../utils/fp";
import { useAppDispatch, useAppSelector } from "../../../../../utils/hooks";
import { downloadAsset } from "../../../../../utils/http";
import {
  useDeleteRevisionMutation,
  useDeleteTicketMutation,
  useDownloadTicketLink,
  useEditProjectMutation,
  useRevisionMutation,
  useShareTicket,
  useShareTicketLink,
} from "../../../../../utils/queries/projects";
import { updateTicketTitle } from "../../../../../utils/slices/punttProjects";
import { setTicket } from "../../../../../utils/slices/ticket";
import { Breadcrumbs } from "../../../components/Breadcrumbs";
import { DeleteTicketDialog } from "../../../components/DeleteTicketDialog";
import {
  ShareDialog,
  type ShareDialogProps,
} from "../../../components/dialogs/ShareDialog";
import { RenameDialog } from "../../../components/UpdateNameDialog";
import { ticketsRoute } from "../../../route";
import { VersionManagerDialog } from "../../components/dialogs/VersionManagerDialog";
import { ticketRoute } from "../route";
import {
  useTicketPageLoaderData,
  useTicketPageNavigate,
  useTicketPageParams,
  useTicketPageSearch,
} from "../useTicketPageRoute";

export default function TicketHeader() {
  // Router methods — route-agnostic so ticketCommentRoute cold deep links work
  const { ticketId } = useTicketPageParams();
  const { version: versionIndex } = useTicketPageSearch();
  const { tickets } = useTicketPageLoaderData();
  const navigate = useTicketPageNavigate();
  const { notify } = useUI();
  const posthog = usePostHog();

  // Slices
  const dispatch = useAppDispatch();
  const totalVersions =
    useAppSelector((state) => state.ticket.value?.totalRevisions) ?? 0;
  const { value: ticket, comments } = useAppSelector((state) => state.ticket);
  const ticketTitle =
    ticketId == "editabledoc" // demo purposes only
      ? "Data Processing Agreement"
      : ticketId == "editabledoc2"
        ? "Joke Document"
        : ticket?.title;
  const versions = useAppSelector((state) => state.ticket.revisions);
  const reversedVersionIndex = useAppSelector(
    (state) => state.ticket.reversedVersionIndex,
  );
  const user = useAppSelector((state) => state.auth.value);

  // Get sidebar state to adjust bubble positioning
  const sidebarOpen = useAppSelector((state) => state.ui.drawerOpen.comments);

  // Queries and mutations
  const renameTicketMutation = useEditProjectMutation(ticketId);
  const deleteRevisionMutation = useDeleteRevisionMutation(ticketId);
  const createVersionMutation = useRevisionMutation();
  const ticketShareLinkMutation = useShareTicketLink();
  const shareTicketMutation = useShareTicket();
  const deleteTicketMutation = useDeleteTicketMutation();
  const downloadTicketMutation = useDownloadTicketLink();

  // Analytics
  const analytics = useAnalytics(`root_ticket_${ticketId}`);

  // Local state
  const [versionManagerDialogOpen, setVersionManagerDialogOpen] =
    useState(false);
  const [createVersionDialogOpen, setCreateVersionDialogOpen] = useState(false);
  const [summarizeDialogOpen, setSummarizeDialogOpen] = useState(false);
  const [renameTicketDialogOpen, setRenameTicketDialogOpen] = useState(false);
  const [shareItem, setShareItem] = useState<ShareDialogProps["item"]>();
  const [shareLink, setShareLink] = useState<string>();
  const [deletingTicketId, setDeletingTicketId] = useState<
    string | undefined
  >();

  const currentVersion = useMemo(() => {
    return versions[reversedVersionIndex!];
  }, [reversedVersionIndex, versions]);

  // Check if current version has AI comments (for the bubble logic fix)
  const currentVersionHasAIComments = useMemo(() => {
    if (!currentVersion?._id || !comments) return false;
    return comments.some((c) => c.isAI && c.boardId === currentVersion._id);
  }, [currentVersion?._id, comments]);

  // Check if current version has time saved data
  const currentVersionHasTimeData = useMemo(() => {
    if (!currentVersion) return false;
    return !!(currentVersion.timeSaved && currentVersion.timeReviewTook);
  }, [currentVersion]);

  // Check if latest revision board has context for showing PM response button
  const latestBoardHasContext = useMemo(() => {
    if (!ticket?.revisionBoards || ticket.revisionBoards.length === 0)
      return false;
    const latestBoard = ticket.revisionBoards[ticket.revisionBoards.length - 1];
    return !!latestBoard.assetContext;
  }, [ticket?.revisionBoards]);

  // Calculate bubble positioning based on sidebar state
  const bubblePosition = useMemo(() => {
    // When sidebar is open, position bubble 1rem from the sidebar's left border (which is at right-0)
    // When sidebar is closed, position bubble 1rem from the collapsed sidebar's left border
    const baseRight = sidebarOpen
      ? "right-[calc(18.75rem+1rem)]"
      : "right-[calc(3.5rem+1rem+1px)]";
    // Increased top spacing to account for breadcrumbs height and maintain visual balance
    const baseTop = "top-32";
    return `${baseRight} ${baseTop}`;
  }, [sidebarOpen]);

  // Effect chain would go here
  useEffect(() => {
    // Only generate share link if user is not a guest
    if (user && !user.referredBy) {
      // Generate share link on page load and when ticket ID changes
      ticketShareLinkMutation.mutate(
        { ticketId, version: "all" },
        {
          onSuccess(data) {
            if (isNil(data)) {
              throw new Error("An unknown error occurred");
            }

            let { url } = data;
            if (versionIndex) {
              url = `${url}&version=${versionIndex}`;
            }

            setShareLink(url);
          },
        },
      );
    }
  }, [ticketId, versionIndex, user]);

  /////
  // No hooks beyond this point
  /////

  function handleRenameTicket(ticketTitle: string) {
    const startTime = performance.now();

    return renameTicketMutation.mutateAsync(
      { title: ticketTitle, _id: ticketId },
      {
        onSuccess(data) {
          const endTime = performance.now();

          analytics.capture("ticket_renamed", {
            ticketTitle: ticketTitle,
            mutationDuration: endTime - startTime,
            durationSincePageLoad:
              endTime - (analytics.startTimeRef?.current ?? 0),
          });

          setRenameTicketDialogOpen(false);

          if (isNil(data)) return;

          dispatch(setTicket(data));
          dispatch(
            updateTicketTitle({ ticketId: ticketId, newTitle: ticketTitle }),
          );
        },
      },
    );
  }

  function handleCreateVersion(assets: Asset[]) {
    const firstAsset = assets[0];
    const { source } = firstAsset;

    createVersionMutation.mutate(
      {
        ticketId,
        payload: {
          reviewFiles: assets,
          documentImportType: source.slice(source.lastIndexOf(".") + 1),
        },
      },
      {
        onSuccess(data) {
          setCreateVersionDialogOpen(false);
          // FIXME: this is not guaranteed to be the board the user just uploaded if
          // two people uploaded versions at the same time.
          const newBoard = data.boards[data.boards.length - 1];

          analytics.capture("created_revision", { boardId: newBoard._id });

          // Reload classic ticket view so the loader picks the newest version.
          // Must use an explicit path — on ticketCommentRoute, navigate({})
          // would re-enter the comment deep link instead of latest version.
          navigate({
            to: ticketRoute.to,
            params: { ticketId },
            replace: true,
          });
        },
      },
    );
  }

  function handleShareTicket(
    ticketId: string,
    emails: string[],
    shareVersion: ShareTicketBody["version"],
  ) {
    const start = performance.now();

    shareTicketMutation.mutate(
      { ticketId, emails, version: shareVersion },
      {
        onSuccess() {
          analytics.capture("generate_share_ticket_link", {
            ticketId,
            recipientCount: emails.length,
            shareVersion,
            duration: performance.now() - start,
          });
          setShareLink(undefined);
          setShareItem(undefined);
          notify({
            title: "Ticket shared",
            message: `Email has been sent to ${emails.length} recipient(s)`,
          });
        },
      },
    );
  }

  function handleShareRequest() {
    setShareLink(undefined);
    setShareItem({ type: "ticket", id: ticketId });
    ticketShareLinkMutation.mutate(
      { ticketId, version: "all" },
      {
        onSuccess(data) {
          if (isNil(data)) {
            throw new Error("An unknown error occurred");
          }

          setShareLink(data.url);
        },
      },
    );
  }

  function handleDownloadTicket(ticketId: string) {
    const startTime = performance.now();
    downloadTicketMutation.mutate(
      { ticketId, boardId: currentVersion._id },
      {
        onSuccess(data) {
          posthog.capture("download_ticket", {
            ticket_id: ticketId,
            ticket_name: ticketTitle,
            data_length: data?.url?.length,
            request_duration: performance.now() - startTime,
          });
          if (data) {
            downloadAsset(data.url);
          }
        },
      },
    );
  }

  function handleCopyLink() {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink);
    notify({
      title: "Share link copied to clipboard",
    });
  }

  const versionManagerList =
    versions.map((board, i) => ({
      _id: board._id,
      title: board.name ?? `Version ${i + 1}`,
      thumbnail: board.thumbnails[0] ?? board.screenshotUrl,
    })) ?? [];

  interface Ticket {
    _id: string;
    title: string;
  }

  const currentTicketIndex = (tickets as Ticket[] | undefined)?.findIndex(
    (t: Ticket) => t._id === ticketId,
  );

  return (
    <>
      <VersionManagerDialog
        open={versionManagerDialogOpen}
        onOpenChange={setVersionManagerDialogOpen}
        onDeleteVersion={(revisionId, index) =>
          deleteRevisionMutation.mutate(revisionId, {
            onSuccess(data) {
              analytics.capture("delete_version", {
                ticketId,
                versionId: revisionId,
              });
              if (isNil(data)) return;

              const { boards } = data;

              if (!boards.length) {
                setVersionManagerDialogOpen(false);
                navigate({
                  to: ticketRoute.to,
                  params: { ticketId },
                  search: {},
                  replace: true,
                });
                return;
              }

              navigate({
                to: ticketRoute.to,
                params: { ticketId },
                search: (prev: { version?: number }) => ({
                  ...prev,
                  version:
                    !isNil(prev.version) && prev.version >= index
                      ? prev.version - 1
                      : prev.version,
                }),
                replace: true,
              });
            },
          })
        }
        revisions={versionManagerList}
        isPending={deleteRevisionMutation.isPending}
        deletingId={deleteRevisionMutation.variables}
      />
      <CreateVersionDialog
        isPending={createVersionMutation.isPending}
        open={createVersionDialogOpen}
        onOpenChange={setCreateVersionDialogOpen}
        onSubmitAssets={handleCreateVersion}
      />
      <SummarizeIntegrationResponseDialog
        open={summarizeDialogOpen}
        onOpenChange={setSummarizeDialogOpen}
        ticketId={ticketId}
      />
      <RenameDialog
        _id={renameTicketDialogOpen ? ticketId : undefined}
        isPending={renameTicketMutation.isPending}
        onCancel={() => setRenameTicketDialogOpen(false)}
        onSave={(_id, name) => handleRenameTicket(name)}
        name={ticketTitle}
      />
      <ShareDialog
        isPending={shareTicketMutation.isPending}
        item={shareItem}
        latestVersionNumber={totalVersions}
        onCancel={() => setShareItem(undefined)}
        onShare={(emails, shareVersion) => {
          if (isNil(shareLink)) return;

          handleShareTicket(ticketId, emails, shareVersion);
        }}
        onVersionChange={(version) =>
          ticketShareLinkMutation.mutate(
            { ticketId: shareItem?.id ?? "", version },
            {
              onSuccess(data) {
                if (isNil(data)) {
                  throw new Error("An unknown error occurred");
                }

                let { url } = data;

                if (version === "all") {
                  url = `${url}&version=${versionIndex}`;
                }

                setShareLink(url);
              },
            },
          )
        }
        shareLink={shareLink}
        shareLinkPending={ticketShareLinkMutation.isPending}
      />
      <DeleteTicketDialog
        isPending={deleteRevisionMutation.isPending}
        ticketId={deletingTicketId}
        onCancel={() => setDeletingTicketId(undefined)}
        onDelete={() => {
          deleteTicketMutation.mutate(
            { ticketId },
            {
              onSuccess() {
                navigate({
                  to: ticketsRoute.to,
                  search: (prev) => {
                    const searchParams = { ...prev };
                    if (ticket?.folder) {
                      // @ts-expect-error TS2339: Open ended searchParams in case we change them
                      searchParams.folderId = ticket.folder;
                    }
                    return searchParams;
                  },
                });
              },
            },
          );
        }}
      />
      {/* Main header content — single grid item.
          Uses the toolbar row's `border-b` as a clean horizontal separator
          instead of an ambient `shadow`, which previously bled down the
          vertical page-rail / canvas divider below the header. */}
      <div
        className="relative z-10 col-span-1 row-span-1 min-w-0 bg-base-white"
        data-testid="ticket-header-container"
      >
        {/* Row 1: Breadcrumb + navigation */}
        <PageHeader className="overflow-hidden">
          <Flex
            gap="3"
            align="center"
            justify="between"
            className="min-w-0 flex-1"
            data-testid="ticket-header_ticket-manager-row"
          >
            <Breadcrumbs
              ticketTitle={ticketTitle}
              ticketId={ticketId}
              onRenameTicket={async (_ticketId, title) => {
                await renameTicketMutation.mutateAsync({
                  _id: ticketId,
                  title,
                });
              }}
              onDeleteTicket={(_ticketId) => {
                setDeletingTicketId(ticketId);
              }}
              onShareTicket={(_ticketId) => {
                handleShareRequest();
              }}
              onDownloadTicket={(_ticketId) => {
                handleDownloadTicket(ticketId);
              }}
              onManageVersions={() => setVersionManagerDialogOpen(true)}
              onDeleteFolder={() => {}}
              onRenameFolder={() => {}}
              onShareFolder={() => {}}
              onDownloadFolder={() => {}}
              onMoveToFolder={() => {}}
            />

            <Flex
              gap="4"
              className={cx("shrink-0", {
                hidden:
                  isNil(tickets) ||
                  isNil(user?.email) ||
                  !canAccessPunttDashboard(),
              })}
            >
              <Button
                variant="outline"
                size="1"
                color="gray"
                disabled={
                  currentTicketIndex === 0 ||
                  (!isNil(tickets) && (tickets as Ticket[]).length === 1)
                }
                data-testid="ticket-header-previous-button"
                onClick={() =>
                  navigate({
                    to: ticketRoute.to,
                    // @ts-expect-error TS18048: Button is disabled anyway, so
                    // it's fine
                    params: { ticketId: tickets?.[currentTicketIndex - 1]._id },
                  })
                }
              >
                <ArrowLeft />
                Previous asset
              </Button>
              <Button
                variant="outline"
                size="1"
                color="gray"
                disabled={
                  !isNil(tickets) &&
                  (currentTicketIndex === (tickets as Ticket[]).length - 1 ||
                    (tickets as Ticket[]).length === 1)
                }
                data-testid="ticket-header-next-button"
                onClick={() =>
                  navigate({
                    to: ticketRoute.to,
                    // @ts-expect-error TS18048: Button is disabled anyway, so
                    // it's fine
                    params: { ticketId: tickets?.[currentTicketIndex + 1]._id },
                  })
                }
              >
                Next asset
                <ArrowRight />
              </Button>
            </Flex>
            {currentVersionHasAIComments && currentVersionHasTimeData && (
              <TimeSavedBubble
                timeSaved={currentVersion?.timeSaved ?? 0}
                timeReviewTook={currentVersion?.timeReviewTook ?? 0}
                className={bubblePosition}
                opacity={0.5}
                hoverOpacity={1}
              />
            )}

            <Button
              variant="outline"
              color="blue"
              size="1"
              className={cx({ hidden: !isNil(user?.email) })}
              data-auth-trigger="login-signup-button"
            >
              <UserCircle /> Log In or Sign Up
            </Button>
          </Flex>
        </PageHeader>

        {/* Row 2: Version / toolbar */}
        <Flex
          gap="2"
          align="center"
          data-testid="ticket-header_version-manager-row"
          className="h-14 shrink-0 border-b border-puntt-neutral-gray-6 bg-base-white px-5"
        >
          <Flex
            gap="2"
            align="center"
            className="w-full justify-between whitespace-nowrap"
          >
            <Flex gap="2" align="center">
              <VersionSelect />
              <MissingFontsIndicator />
              <AuthTooltip
                trigger="upload-version-cta"
                classNames="cursor-pointer"
              >
                <Button
                  variant="outline"
                  size="1"
                  onClick={() => setCreateVersionDialogOpen(true)}
                  disabled={!canUploadTickets()}
                  data-testid="ticket-header_upload-version-cta"
                  className={cx("border-[#3C39EC] text-[#3C39EC]", {
                    hidden: !canUploadTickets(),
                  })}
                >
                  <UploadSimple /> Upload New Version
                </Button>
              </AuthTooltip>
              {(user?.role === EnterpriseProfileType.MEANINGFUL_GIGS ||
                user?.role === EnterpriseProfileType.CATALYST_AI) &&
                latestBoardHasContext && (
                  <AuthTooltip
                    trigger="submit-pm-response-cta"
                    classNames="cursor-pointer"
                  >
                    <Button
                      variant="outline"
                      size="1"
                      onClick={() => setSummarizeDialogOpen(true)}
                      data-testid="ticket-header_submit-pm-response-cta"
                      className="border-[#3C39EC] text-[#3C39EC]"
                    >
                      <Export /> Submit response to PM tool
                    </Button>
                  </AuthTooltip>
                )}
              <div className="flex h-6 flex-row items-center overflow-hidden rounded-[4.5px] p-0">
                <AuthTooltip classNames="cursor-pointer">
                  <Button
                    size="1"
                    variant="outline"
                    className="h-6 min-w-0 !rounded-none !rounded-l-[4.5px] border-r-0 border-[#3C39EC] px-2 text-[#3C39EC]"
                    disabled={!versions.length}
                    data-testid="ticket-header-share-button"
                    onClick={handleShareRequest}
                  >
                    <ShareNetwork className="mr-1" /> Share
                  </Button>
                </AuthTooltip>
                <AuthTooltip classNames="cursor-pointer">
                  <Button
                    size="1"
                    variant="outline"
                    className="-ml-px h-6 min-w-0 !rounded-none !rounded-r-[4.5px] border-l-0 border-[#3C39EC] px-2 text-[#3C39EC]"
                    onClick={handleCopyLink}
                    disabled={!shareLink}
                    data-testid="ticket-header-copy-link-button"
                    aria-label="Copy Share Link"
                  >
                    <LinkIcon />
                  </Button>
                </AuthTooltip>
              </div>
            </Flex>
          </Flex>
        </Flex>
      </div>
    </>
  );
}
